import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase, Profile, Company } from '../lib/supabase';

// Estado unificado — se actualiza en una sola llamada, sin renders intermedios
type AuthState =
  | { status: 'loading' }
  | { status: 'unauthenticated' }
  | { status: 'no_profile';  user: User; session: Session }
  | { status: 'ready';       user: User; session: Session; profile: Profile; company: Company | null };

interface AuthContextType {
  // Conveniencias para no cambiar todos los componentes
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  company: Company | null;
  loading: boolean;
  noProfile: boolean;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signUp: (email: string, password: string, fullName: string, companyData: Partial<Company>) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  refreshCompany: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: 'loading' });

  useEffect(() => {
    let mounted = true;

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!mounted) return;
      if (session?.user) {
        loadUserData(session.user, session);
      } else {
        setState({ status: 'unauthenticated' });
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!mounted) return;
      if (session?.user) {
        loadUserData(session.user, session);
      } else {
        setState({ status: 'unauthenticated' });
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  async function loadUserData(user: User, session: Session) {
    try {
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .maybeSingle();

      if (profileError) {
        console.error('Error loading profile:', profileError.message);
        // Un solo setState — nunca hay estado intermedio visible
        setState({ status: 'no_profile', user, session });
        return;
      }

      if (!profileData) {
        setState({ status: 'no_profile', user, session });
        return;
      }

      let company: Company | null = null;
      if (profileData.company_id) {
        const { data: companyData } = await supabase
          .from('companies')
          .select('*')
          .eq('id', profileData.company_id)
          .maybeSingle();
        company = companyData ?? null;
      }

      // Un solo setState con todo listo — React renderiza una sola vez
      setState({ status: 'ready', user, session, profile: profileData, company });

    } catch (err) {
      console.error('Error in loadUserData:', err);
      setState({ status: 'no_profile', user, session });
    }
  }

  async function refreshCompany() {
    if (state.status !== 'ready') return;
    const { data } = await supabase
      .from('companies')
      .select('*')
      .eq('id', state.profile.company_id)
      .maybeSingle();
    if (data) {
      setState({ ...state, company: data });
    }
  }

  async function signIn(email: string, password: string) {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error };
  }

  async function signUp(
    email: string,
    password: string,
    fullName: string,
    companyData: Partial<Company>
  ) {
    const { data: authData, error: authError } = await supabase.auth.signUp({ email, password });
    if (authError) return { error: authError };
    if (!authData.user) return { error: new Error('No user returned') };

    const { data: newCompany, error: companyError } = await supabase
      .from('companies')
      .insert({
        name: companyData.name,
        ruc: companyData.ruc,
        regime: companyData.regime || 'general',
        email,
        rmv: 1025.00,
        essalud_rate: 9.00,
        snp_rate: 13.00,
        afp_system: 'onp',
        assign_familiar: true,
        working_days_per_month: 26,
        daily_work_hours: 8,
      })
      .select()
      .single();

    if (companyError) return { error: companyError };

    const { error: profileError } = await supabase
      .from('profiles')
      .insert({
        id: authData.user.id,
        company_id: newCompany.id,
        full_name: fullName,
        role: 'admin',
      });

    if (profileError) return { error: profileError };

    await supabase.from('company_settings').upsert({
      company_id: newCompany.id,
      check_in_time: '08:00:00',
      check_out_time: '17:00:00',
      tolerance_minutes: 5,
      overtime_rate: 1.25,
      overtime_rate_sunday: 2.00,
      currency: 'PEN',
      timezone: 'America/Lima',
    }, { onConflict: 'company_id' });

    return { error: null };
  }

  async function signOut() {
    await supabase.auth.signOut();
  }

  // Derivar conveniencias del estado unificado
  const user    = state.status === 'ready' || state.status === 'no_profile' ? state.user    : null;
  const session = state.status === 'ready' || state.status === 'no_profile' ? state.session : null;
  const profile = state.status === 'ready' ? state.profile : null;
  const company = state.status === 'ready' ? state.company : null;
  const loading  = state.status === 'loading';
  const noProfile = state.status === 'no_profile';

  return (
    <AuthContext.Provider value={{
      user, session, profile, company, loading, noProfile,
      signIn, signUp, signOut, refreshCompany,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
